import { useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { UXPContext } from '@core/context/Context';
/** No mouse / keyboard / touch / scroll for this long → one 'idle' event; next input → 'active'. */
const IDLE_MS = 10 * 60000;
/** Re-arm the idle timer at most this often so a mousemove storm stays cheap. */
const REARM_MS = 1000;
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'pointerdown', 'touchstart', 'scroll', 'wheel'];
/**
 * Page-view analytics. Sends one event per route change.
 * It also sends tab visibility (`hidden` / `visible`) and inactivity (`idle` / `active`) events.
 * The backend derives time-on-page from them.
 * The server identifies the session from the API key. Nothing else identifying is sent.
 *
 * Each event is its own `POST api/pageview/track`. It is never part of a batch.
 * The call is not awaited and its result is ignored. It ends after 5 seconds.
 *
 * Nothing is sent unless the host set `context.pageViewTracking` at page load.
 * An older server does not set it, so it gets no request. Public sessions send nothing.
 */
export function usePageViewTracker({ matchedRoute, isLoading }) {
    const context = useContext(UXPContext);
    const location = useLocation();
    const enabled = !context.isPublicSession && !!context.apiKey && context.pageViewTracking === true;
    const lastViewKey = useRef('');
    const idleTimer = useRef(null);
    const lastArm = useRef(0);
    const isIdle = useRef(false);
    // Kept in refs so the listener effect below doesn't re-subscribe on every navigation.
    const send = useRef(() => { });
    send.current = (type) => {
        var _a;
        if (!enabled)
            return;
        // Own request, never batched. Not awaited. A failure must not reach the page.
        context.executeAPISafe({
            url: 'api/pageview/track',
            method: 'POST',
            data: {
                type,
                url: location.pathname,
                query: location.search,
                // Route title: the backend keeps it as the page's label in the filter pick list.
                title: ((_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.title) || '',
            },
            timeout: 5000,
        }).catch(() => { });
    };
    const armIdle = useRef(() => { });
    armIdle.current = (force = false) => {
        const now = Date.now();
        if (!force && idleTimer.current && now - lastArm.current < REARM_MS)
            return;
        if (idleTimer.current)
            clearTimeout(idleTimer.current);
        idleTimer.current = null;
        if (document.visibilityState === 'hidden')
            return;
        lastArm.current = now;
        idleTimer.current = setTimeout(() => {
            isIdle.current = true;
            send.current('idle');
        }, IDLE_MS);
    };
    // One 'view' per distinct pathname+search, once routing has settled and it's not a redirect hop.
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        if (!enabled || isLoading)
            return;
        const redirectTo = (_b = (_a = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _a === void 0 ? void 0 : _a.redirectTo) !== null && _b !== void 0 ? _b : (_e = (_d = (_c = matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.config) === null || _c === void 0 ? void 0 : _c.metadata) === null || _d === void 0 ? void 0 : _d.redirect) === null || _e === void 0 ? void 0 : _e.to;
        if (redirectTo)
            return;
        const key = location.pathname + location.search;
        if (key === lastViewKey.current)
            return;
        lastViewKey.current = key;
        isIdle.current = false; // navigating is activity; the backend clamps any open idle span at this view
        armIdle.current(true);
        send.current('view');
    }, [enabled, isLoading, matchedRoute === null || matchedRoute === void 0 ? void 0 : matchedRoute.matchedPath, location.pathname, location.search]);
    // Attention events: tab visibility + inactivity.
    useEffect(() => {
        if (!enabled)
            return;
        const onActivity = () => {
            if (isIdle.current) {
                isIdle.current = false;
                send.current('active');
            }
            armIdle.current();
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                if (idleTimer.current)
                    clearTimeout(idleTimer.current);
                idleTimer.current = null;
                send.current('hidden');
            }
            else {
                send.current('visible');
                armIdle.current(true);
            }
        };
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
        document.addEventListener('visibilitychange', onVisibility);
        armIdle.current(true);
        return () => {
            if (idleTimer.current)
                clearTimeout(idleTimer.current);
            idleTimer.current = null;
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [enabled]);
}
